// LAY-02 (bed speaker angle) and LAY-03 (height speaker angle) — README.md.

import type { BuildContext } from '../context';
import type { Channel, Issue } from '../types';
import { azimuthDeg, elevationArcDeg } from '../geometry';
import { CHANNEL_NAME, fmtDeg, issueId } from '../copy';

const GRACE_DEG = 5;

interface RangeSpec { min: number; max: number }

function bedRange(channel: Channel, layout: string): RangeSpec | null {
  switch (channel) {
    case 'FL': case 'FR': return { min: 22, max: 30 };
    case 'C': return { min: 0, max: 10 };
    case 'SL': case 'SR': return layout.startsWith('7.1') ? { min: 90, max: 110 } : { min: 110, max: 120 };
    case 'SBL': case 'SBR': return { min: 135, max: 150 };
    default: return null;
  }
}

/** Default sign (left = negative, right = positive) for a channel that
 *  measures exactly 0° azimuth, so the fix can still name a side. */
function defaultSign(channel: Channel): 1 | -1 {
  return channel === 'FR' || channel === 'SR' || channel === 'SBR' ? 1 : -1;
}

export function checkLay02(ctx: BuildContext): Issue[] {
  if (!ctx.listener) return [];
  const [lx, , lz] = ctx.listener.position;
  const layout = ctx.doc.speakerSetup.layout;
  const issues: Issue[] = [];

  for (const id of ctx.bedIds) {
    const channel = ctx.channels[id];
    const range = bedRange(channel, layout);
    if (!range) continue;
    const entry = ctx.doc.speakers.find((s) => s.id === id);
    if (!entry) continue;
    const az = azimuthDeg(ctx.doc, entry.position[0] - lx, entry.position[2] - lz);
    const abs = Math.abs(az);
    if (abs >= range.min - GRACE_DEG && abs <= range.max + GRACE_DEG) continue;

    const sign = abs === 0 ? defaultSign(channel) : Math.sign(az);
    const clamped = Math.min(range.max, Math.max(range.min, abs));
    const target = sign * clamped;
    issues.push({
      id: issueId('LAY-02', [id]),
      rule: 'LAY-02',
      severity: 'warning',
      text: `${CHANNEL_NAME[channel]} is at ${fmtDeg(abs)}°.`,
      fix: `Move it to ${fmtDeg(target)}° from your seat.`,
      subjects: [id],
    });
  }
  return issues;
}

type Category = 'TF' | 'TM' | 'TR';

function categoryOf(channel: Channel): Category | null {
  if (channel === 'TFL' || channel === 'TFR') return 'TF';
  if (channel === 'TML' || channel === 'TMR') return 'TM';
  if (channel === 'TRL' || channel === 'TRR') return 'TR';
  return null;
}

const CATEGORY_RANGE: Record<Category, RangeSpec> = {
  TF: { min: 30, max: 55 },
  TM: { min: 65, max: 100 },
  TR: { min: 125, max: 150 },
};
const CATEGORY_TARGET: Record<Category, number> = { TF: 45, TM: 80, TR: 135 };

export function checkLay03(ctx: BuildContext): Issue[] {
  if (!ctx.listener) return [];
  const [lx, ly, lz] = ctx.listener.position;
  const layout = ctx.doc.speakerSetup.layout;
  const isDotTwo = layout === 'stereo.2' || layout === '5.1.2' || layout === '7.1.2';
  const issues: Issue[] = [];

  for (const id of ctx.overheadIds) {
    const channel = ctx.channels[id];
    const category = categoryOf(channel);
    if (!category) continue;
    const entry = ctx.doc.speakers.find((s) => s.id === id);
    if (!entry) continue;
    const dx = entry.position[0] - lx;
    const dz = entry.position[2] - lz;
    const dy = entry.position[1] - ly;
    const horiz = Math.hypot(dx, dz);
    const arc = elevationArcDeg(ctx.doc, dx, dz, dy);

    const range = category === 'TM' && isDotTwo ? { min: 30, max: 100 } : CATEGORY_RANGE[category];
    if (arc >= range.min - GRACE_DEG && arc <= range.max + GRACE_DEG) continue;

    const target = CATEGORY_TARGET[category];
    const tooSmall = arc < target;
    // Ambiguous when the speaker sits (near) directly overhead — front/back,
    // and hence the fix direction, is undefined at horizontal distance ~0.
    const ambiguous = horiz < 1e-3;

    let fix: string;
    if (ambiguous) {
      fix = `Move it until it is ${fmtDeg(target)}° above your seat.`;
    } else {
      // TF/TM: too small -> further from the screen, too large -> closer.
      // TR: the reverse (measured behind the seat).
      const isTfTm = category === 'TF' || category === 'TM';
      const wantsFurther = isTfTm ? tooSmall : !tooSmall;
      fix = `Move it ${wantsFurther ? 'further from' : 'closer to'} the screen until it is ${fmtDeg(target)}°.`;
    }

    issues.push({
      id: issueId('LAY-03', [id]),
      rule: 'LAY-03',
      severity: 'warning',
      text: `${CHANNEL_NAME[channel]} is ${fmtDeg(arc)}° above your seat.`,
      fix,
      subjects: [id],
    });
  }
  return issues;
}
